import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';
import { tenantCacheTag } from '../../utils/tenantCacheTag';
import { getTenantId, getSubdomain } from '../../utils/tenantContext';
import type { RootState } from '../index';

function decodeJwt(token: string | null): any {
  if (!token) return {};
  try {
    // Guard non-JWT strings
    if (token.split('.').length !== 3) return {};    
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return {};
  }
}

export interface Service {
  id: string;
  name: string;
  description?: string;
  category?: string;
  status?: string;
  pricing?: any;
  marketplace_id?: string;
  marketplace_linkage?: any;
  total_imports?: number;
  marketplace_commission_rate?: number;
  is_flagship?: boolean;
  commission_eligible?: boolean;
  commission_percentage?: number;
  company_name?: string;
  average_rating?: number;
  review_count?: number;
  base_price?: number;
  expected_install_time_days?: number;
  validation_days_estimate?: number;
  expected_availability_time_hours?: number;
  expected_contract_hours?: number;
  metadata?: any;
}

const selectTenantId = (state: RootState) =>
  state.auth.user?.tenantId ?? (state as any).auth?.user?.tenant_id ?? null;
function resolveTenantId(state: RootState): string | null {
  const id = selectTenantId(state);
  if (id) return id;
  const token =
    state.auth.token ||
    (typeof window !== 'undefined' ? window.localStorage.getItem('token') : null);
  const decoded = decodeJwt(token);
  return decoded?.tenantId || null;
}

function currentTenantTag(state?: RootState): string {
  try {
    const tenantId = getTenantId(state);
    const sub = getSubdomain(state as any);
    return tenantCacheTag(sub, tenantId);
  } catch {
    const tenantId = getTenantId();
    const sub = getSubdomain();
    return tenantCacheTag(sub, tenantId);
  }
}

export const servicesApi = createApi({
  reducerPath: 'servicesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Service'],
  invalidationBehavior: 'immediately',
  endpoints: (builder) => ({
    getServices: builder.query<Service[], string | { key: string; includeDeleted?: boolean }>({
      query: (arg) => {
        const showDeleted = typeof arg === 'object' && !!arg?.includeDeleted;
        return { url: '/services', params: { status: showDeleted ? 'archived' : 'active' } };
      },
      // use the tenantTag query arg so mutations can invalidate this cache
      providesTags: (_result, _error, arg) => [
        { type: 'Service', id: (typeof arg === 'object' ? arg.key : arg) },
      ],
      async onQueryStarted(arg, { queryFulfilled, getState }) {
        const state = getState() as RootState;
        const token = state.auth.token;
        const sub = state.tenant.subdomain;
        const tenantId = resolveTenantId(state);
        if (!tenantId) {
          console.warn('[RTK][getServices] missing tenantId');
        }
        console.log('[RTK][getServices] start', { arg, token: token?.slice(0, 10), sub, tenantId });
        try {
          const { data } = await queryFulfilled;
          console.log('[RTK][getServices] success', { arg, count: data.length });
        } catch (err) {
          console.warn('[RTK][getServices] error', { arg, err });
        }
      },
    }),
    getService: builder.query<Service, string>({
      query: (id) => ({ url: `/services/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Service', id }],
      async onQueryStarted(id, { queryFulfilled, getState }) {
        const state = getState() as RootState;
        const token = state.auth.token;
        const sub = state.tenant.subdomain;
        const tenantId = resolveTenantId(state);
        console.log('[RTK][getService] start', { id, token: token?.slice(0, 10), sub, tenantId });
        try {
          const { data } = await queryFulfilled;
          console.log('[RTK][getService] success', { id, serviceName: data.name });
        } catch (err) {
          console.warn('[RTK][getService] error', { id, err });
        }
      },
    }),
    createService: builder.mutation<Service, Partial<Service>>({
      query: (body) => ({
        url: '/services',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState;
        const tenantTag = currentTenantTag(state);
        const tempId = `temp-${Date.now()}`;
        const patch = dispatch(
          servicesApi.util.updateQueryData('getServices', tenantTag, (draft) => {
            (draft as Service[]).push({ ...(body as Service), id: tempId });
          })
        );
        try {
          const { data } = await queryFulfilled;
          patch.undo();
          dispatch(
            servicesApi.util.updateQueryData('getServices', tenantTag, (draft) => {
              draft.push(data as Service);
            })
          );
          // Redux cache now contains the new service immediately
          console.log('[RTK][createService] success', { tenantTag, id: data.id });
        } catch {
          patch.undo();
          console.warn('[RTK][createService] error');
        }
      },
      // invalidate the tenant-specific service list so it refetches from the server
      invalidatesTags: () => [{ type: 'Service', id: currentTenantTag() }],
    }),
    updateService: builder.mutation<Service, { id: string; data: Partial<Service> }>({
      query: ({ id, data }) => ({
        url: `/services/${id}`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState;
        const tenantTag = currentTenantTag(state);
        console.log('[RTK][updateService] invalidating', { tenantTag, id });
        dispatch(servicesApi.util.invalidateTags([{ type: 'Service', id: tenantTag }]));
        dispatch(
          servicesApi.endpoints.getServices.initiate(tenantTag, { forceRefetch: true })
        );
        console.log('[RTK][updateService] refetch triggered', { tenantTag, id });
        const patch = dispatch(
          servicesApi.util.updateQueryData('getServices', tenantTag, (draft) => {
            const svc = draft.find((s) => s.id === id);
            if (svc) Object.assign(svc, data);
          })
        );
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            servicesApi.util.updateQueryData('getServices', tenantTag, (draft) => {
              const idx = draft.findIndex((s) => s.id === id);
              if (idx !== -1) draft[idx] = updated as Service;
            })
          );
          console.log('[RTK][updateService] success', { tenantTag, id: updated.id });
        } catch {
          patch.undo();
          console.warn('[RTK][updateService] error', { tenantTag, id });
        }
      },
      invalidatesTags: () => [],
    }),
    deleteService: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/services/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Service', id: currentTenantTag() }],
    }),

    restoreService: builder.mutation<{ restored: string }, string>({
      query: (id) => ({
        url: `/services/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Service', id: currentTenantTag() }],
    }),

    setServiceFlagship: builder.mutation<
      { id: string; flagship_promoted: boolean; promoted_at: string | null; promoted_by: string | null },
      { id: string; flagship_promoted: boolean }
    >({
      query: ({ id, flagship_promoted }) => ({
        url: `/services/${id}/flagship`,
        method: 'PUT',
        body: { flagship_promoted },
      }),
      invalidatesTags: () => [{ type: 'Service', id: 'LIST' }], // Invalidate all service lists
    }),

    setServiceCompanyProfile: builder.mutation<
      { id: string; provider_company_profile: any },
      { id: string; company_profile: any }
    >({
      query: ({ id, company_profile }) => ({
        url: `/super-admin/services/${id}/company-profile`,
        method: 'PUT',
        body: { company_profile },
      }),
      invalidatesTags: () => [{ type: 'Service', id: 'LIST' }], // Invalidate all service lists
    }),

  }),
});

export const {
  useGetServicesQuery,
  useGetServiceQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useRestoreServiceMutation,
  useSetServiceFlagshipMutation,
  useSetServiceCompanyProfileMutation,
} = servicesApi;
